import { BETA_MAP_SLUG_RE, parseChallengeNumber } from "@dailywars/core/engine";
import { mapIdSchema } from "@dailywars/core/engine/types/inputSchemas";
import { notFound } from "@tanstack/react-router";
import * as z from "zod";

import { DOCUMENT_TITLES, documentHead } from "#/lib/documentTitle";

export const replayIdSchema = z.uuid();
const betaMapSlugSchema = z.string().regex(BETA_MAP_SLUG_RE, "Invalid beta map slug.");

export type MapIdRouteParams = { mapId: string };
export type ReplayIdRouteParams = { replayId: string };
export type BetaMapSlugRouteParams = { slug: string };
export type ChallengeNumberRouteParams = { challengeNumber: string };

function parseMapIdOrNotFound(mapId: string): string {
  const result = mapIdSchema.safeParse(mapId);
  if (!result.success) {
    throw notFound();
  }
  return result.data;
}

function parseReplayIdOrNotFound(replayId: string): string {
  const result = replayIdSchema.safeParse(replayId);
  if (!result.success) {
    throw notFound();
  }
  return result.data;
}

function parseBetaSlugOrNotFound(slug: string): string {
  const result = betaMapSlugSchema.safeParse(slug);
  if (!result.success) {
    throw notFound();
  }
  return result.data;
}

export const mapIdRouteParams = {
  parse: (params: MapIdRouteParams): MapIdRouteParams => ({
    mapId: parseMapIdOrNotFound(params.mapId),
  }),
  stringify: ({ mapId }: MapIdRouteParams) => ({ mapId }),
};

export const replayIdRouteParams = {
  parse: (params: ReplayIdRouteParams): ReplayIdRouteParams => ({
    replayId: parseReplayIdOrNotFound(params.replayId),
  }),
  stringify: ({ replayId }: ReplayIdRouteParams) => ({ replayId }),
};

export const betaMapSlugRouteParams = {
  parse: (params: BetaMapSlugRouteParams): BetaMapSlugRouteParams => ({
    slug: parseBetaSlugOrNotFound(params.slug),
  }),
  stringify: ({ slug }: BetaMapSlugRouteParams) => ({ slug }),
};

function parseChallengeNumberOrNotFound(challengeNumber: string): string {
  if (parseChallengeNumber(challengeNumber) === null) {
    throw notFound();
  }
  return challengeNumber;
}

export const challengeNumberRouteParams = {
  parse: (params: ChallengeNumberRouteParams): ChallengeNumberRouteParams => ({
    challengeNumber: parseChallengeNumberOrNotFound(params.challengeNumber),
  }),
  stringify: ({ challengeNumber }: ChallengeNumberRouteParams) => ({ challengeNumber }),
};

export function mapIdRouteHead({
  mapId,
  titleForMapId,
  descriptionForMapId,
  pathForMapId,
  imageUrlForMapId,
  robotsForMapId,
}: {
  mapId: string;
  titleForMapId: (validMapId: string) => string;
  descriptionForMapId: (validMapId: string) => string;
  pathForMapId: (validMapId: string) => string;
  imageUrlForMapId?: (validMapId: string) => string;
  robotsForMapId?: (validMapId: string) => string | undefined;
}) {
  const result = mapIdSchema.safeParse(mapId);
  if (!result.success) {
    return documentHead(DOCUMENT_TITLES.home);
  }

  const validMapId = result.data;
  return documentHead(titleForMapId(validMapId), {
    description: descriptionForMapId(validMapId),
    canonicalPath: pathForMapId(validMapId),
    imageUrl: imageUrlForMapId?.(validMapId),
    robots: robotsForMapId?.(validMapId),
  });
}
